/**
 * `TaskActions` — the action row on a task detail.
 *
 * SOFT-DELETE ONLY is a hard platform rule: there is NO delete affordance
 * anywhere. The only removal-shaped action is `archive`, offered ONLY on a
 * terminal status (done/dropped); an archived task can be `reopen`ed. Status
 * changes go through `update` with an optional note; a free note goes through
 * `note`.
 *
 * Mutations return the updated task, and `onChanged` asks the parent to
 * refetch (the `tasklist.changed` broadcast also drives this, but the direct
 * call keeps the surface responsive without waiting on the round-trip).
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { cn } from "@/lib/utils";
import {
  archiveTask,
  reopenTask,
  updateTask,
  noteTask,
  TERMINAL_STATUSES,
  type TaskRow,
  type TaskStatus,
} from "@/api/lx-tasklist";

const STATUS_FLOW: TaskStatus[] = ["open", "waiting", "done", "dropped"];

export function TaskActions({
  task,
  onChanged,
}: {
  task: TaskRow;
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [noteOpen, setNoteOpen] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [error, setError] = useState<string | null>(null);

  const isArchived = Boolean(task.audit.archivedAt);
  const isTerminal = TERMINAL_STATUSES.includes(task.status);

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      onChanged();
    } catch (err) {
      // Field-named validation errors are surfaced verbatim (no coercion).
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-3 rounded border border-cream-200/60 bg-cream-50/40 p-2 dark:border-coffee-800/60 dark:bg-coffee-900/30">
      {/* Status transitions — only the statuses this task is not already in. */}
      <div className="flex flex-wrap items-center gap-1">
        {STATUS_FLOW.filter((s) => s !== task.status && !isArchived).map((s) => (
          <button
            key={s}
            type="button"
            data-testid={`task-set-status-${s}`}
            disabled={busy}
            onClick={() => void run(() => updateTask(task.id, { status: s }))}
            className="rounded border border-cream-300 px-2 py-0.5 text-[12px] text-slate-600 hover:bg-cream-100 disabled:opacity-50 dark:border-coffee-700 dark:text-slate-300 dark:hover:bg-coffee-800"
          >
            {t(`tasklist.setStatus.${s}`)}
          </button>
        ))}

        <button
          type="button"
          data-testid="task-add-note"
          disabled={busy}
          onClick={() => setNoteOpen((v) => !v)}
          className="rounded border border-cream-300 px-2 py-0.5 text-[12px] text-slate-600 hover:bg-cream-100 disabled:opacity-50 dark:border-coffee-700 dark:text-slate-300 dark:hover:bg-coffee-800"
        >
          {t("tasklist.addNote")}
        </button>

        {/* Archive is offered ONLY on a terminal status; reopen on an
            archived one. There is deliberately no delete. */}
        {isTerminal && !isArchived && (
          <button
            type="button"
            data-testid="task-archive"
            disabled={busy}
            onClick={() => void run(() => archiveTask(task.id))}
            className="rounded border border-cream-300 px-2 py-0.5 text-[12px] text-slate-500 hover:bg-cream-100 disabled:opacity-50 dark:border-coffee-700 dark:text-slate-400 dark:hover:bg-coffee-800"
          >
            {t("tasklist.archive")}
          </button>
        )}
        {isArchived && (
          <button
            type="button"
            data-testid="task-reopen"
            disabled={busy}
            onClick={() => void run(() => reopenTask(task.id))}
            className="rounded border border-cream-300 px-2 py-0.5 text-[12px] text-slate-600 hover:bg-cream-100 disabled:opacity-50 dark:border-coffee-700 dark:text-slate-300 dark:hover:bg-coffee-800"
          >
            {t("tasklist.reopen")}
          </button>
        )}
      </div>

      {noteOpen && (
        <div className="mt-2 flex gap-1">
          <input
            data-testid="task-note-input"
            type="text"
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            placeholder={t("tasklist.notePlaceholder")}
            className="min-w-0 flex-1 rounded border border-cream-300 bg-white px-2 py-1 text-[13px] outline-none focus:border-blue-400 dark:border-coffee-700 dark:bg-coffee-900 dark:text-slate-100"
          />
          <button
            type="button"
            data-testid="task-note-save"
            disabled={busy || noteText.trim() === ""}
            onClick={() =>
              void run(async () => {
                await noteTask(task.id, noteText.trim());
                setNoteText("");
                setNoteOpen(false);
              })
            }
            className={cn(
              "rounded px-2 py-1 text-[13px] font-medium text-white",
              busy || noteText.trim() === ""
                ? "cursor-not-allowed bg-blue-300 dark:bg-blue-900/50"
                : "bg-blue-600 hover:bg-blue-700",
            )}
          >
            {t("common.save")}
          </button>
        </div>
      )}

      {error && (
        <div
          data-testid="task-action-error"
          role="alert"
          className="mt-2 rounded border border-red-200/70 bg-red-50 px-2 py-1 text-[13px] text-red-700 dark:border-red-900/50 dark:bg-red-950/40 dark:text-red-300"
        >
          {error}
        </div>
      )}
    </div>
  );
}
